import React, { useState, useEffect, useRef } from 'react';
import { Coins, Sprout, Timer, CheckCircle, Lock, CloudRain, Wheat, Pickaxe, Leaf } from 'lucide-react';
import { useStore } from '../../store/useStore';
import { Card } from '../common/Card';
import { Button } from '../common/Button';
import { CircularProgress } from '../common/CircularProgress';
import type { CropType } from '../../types';
import { FARMING_DATA, PLOT_UNLOCK_LEVELS } from '../../data/farming';

// Farming plots unlock at these levels
// Imported from data

interface FarmingPlotProps {
    plot: { id: number; unlocked: boolean; crop?: any; assignedGnome?: boolean };
    selectedSeed: CropType;
    onInteract: () => void;
    onGnomeToggle: () => void;
    onMutate: () => void;
    canMutate: boolean;
    skills: any;
    maxGnomes: number;
}

const FarmingPlot = ({ plot, selectedSeed, onInteract, onGnomeToggle, onMutate, canMutate, skills, maxGnomes }: FarmingPlotProps) => {
    // Helper to determine if a plot is unlocked based on level
    const currentFarmingLevel = skills.farming?.level ?? 1;

    // Logic for unlock display
    // Indices 0, 1, 2 use Level requirements.
    // Indices 3, 4 (and beyond) use Upgrade requirements.
    const unlockLevelRequired = PLOT_UNLOCK_LEVELS[plot.id];
    const isLevelUnlock = unlockLevelRequired !== undefined;

    // Determine effective unlock status
    const isUnlocked = plot.unlocked || (isLevelUnlock && currentFarmingLevel >= unlockLevelRequired);

    const isReady = plot.crop && Date.now() >= plot.crop.plantedAt + plot.crop.duration;
    const progress = plot.crop
        ? Math.min(100, ((Date.now() - plot.crop.plantedAt) / plot.crop.duration) * 100)
        : 0;

    const seedColor = plot.crop ? FARMING_DATA.find(c => c.id === plot.crop.type)?.color : 'transparent';

    return (
        <div
            className={`farming-plot-slot ${!isUnlocked ? 'locked' : ''} ${plot.crop ? 'planted' : ''} ${isReady ? 'ready' : ''}`}
            onClick={onInteract}
        >
            <div className="soil-texture" />

            {/* GNOME TOGGLE */}
            {isUnlocked && maxGnomes > 0 && (
                <div
                    onClick={(e) => {
                        e.stopPropagation();
                        onGnomeToggle();
                    }}
                    style={{
                        position: 'absolute',
                        top: '8px',
                        right: '8px',
                        zIndex: 10,
                        cursor: 'pointer',
                        background: plot.assignedGnome ? 'rgba(34, 197, 94, 0.2)' : 'rgba(0,0,0,0.3)',
                        borderRadius: '50%',
                        padding: '4px',
                        border: `1px solid ${plot.assignedGnome ? 'var(--color-success)' : 'transparent'}`,
                        transition: 'all 0.2s'
                    }}
                    title={plot.assignedGnome ? "Gnome Active" : "Assign Gnome"}
                >
                    <span style={{ fontSize: '1rem', filter: plot.assignedGnome ? 'none' : 'grayscale(1)' }}>🧙‍♂️</span>
                </div>
            )}

            {/* LOCKED STATE */}
            {!isUnlocked && (
                <div className="plot-content" style={{ opacity: 0.8 }}>
                    <Lock size={28} color="var(--color-text-muted)" />
                    <div style={{ fontSize: '0.8rem', color: 'var(--color-text-secondary)', marginTop: '8px', textAlign: 'center' }}>
                        {isLevelUnlock ? (
                            <>
                                Unlocks at<br />
                                <span style={{ color: 'var(--color-skill-farming)', fontWeight: 'bold' }}>Lvl {unlockLevelRequired}</span>
                            </>
                        ) : (
                            <>
                                Unlocks via<br />
                                <span style={{ color: 'var(--color-accent)', fontWeight: 'bold' }}>Upgrade</span>
                            </>
                        )}
                    </div>
                </div>
            )}

            {/* EMPTY STATE */}
            {isUnlocked && !plot.crop && (
                <div className="plot-content" style={{ opacity: 0.6 }}>
                    <div
                        style={{
                            width: '48px',
                            height: '48px',
                            borderRadius: '50%',
                            border: '2px dashed var(--color-border)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                        }}
                    >
                        <Sprout size={24} color="var(--color-text-muted)" />
                    </div>
                    <span style={{ fontSize: '0.8rem', color: 'var(--color-text-muted)', marginTop: '4px' }}>
                        Plant {selectedSeed}
                    </span>
                </div>
            )}

            {/* PLANTED STATE */}
            {plot.crop && (
                <div className="plot-content">
                    <div className="plot-icon-wrapper">
                        {/* Circle Background for Crop */}
                        <div style={{
                            position: 'absolute',
                            inset: 0,
                            borderRadius: '50%',
                            background: seedColor,
                            opacity: 0.2,
                            filter: 'blur(8px)'
                        }} />

                        <CircularProgress
                            progress={progress}
                            size={56}
                            strokeWidth={4}
                            color={isReady ? 'var(--color-success)' : plot.crop.isMutated ? 'var(--color-warning)' : seedColor || 'var(--color-accent)'}
                        >
                            {isReady ? (
                                <CheckCircle size={28} color="var(--color-success)" style={{ filter: 'drop-shadow(0 0 4px rgba(34,197,94,0.5))' }} />
                            ) : (
                                <Leaf size={24} color={seedColor} />
                            )}
                        </CircularProgress>
                    </div>

                    <div style={{ textAlign: 'center', zIndex: 2 }}>
                        <div style={{
                            fontSize: '0.9rem',
                            fontWeight: 700,
                            textTransform: 'capitalize',
                            color: plot.crop.isMutated ? 'var(--color-warning)' : 'var(--color-text-primary)'
                        }}>
                            {plot.crop.isMutated && ' Mutant '}
                            {plot.crop.type}
                        </div>
                        {!isReady && (
                            <div style={{ fontSize: '0.75rem', color: 'var(--color-text-secondary)', fontFamily: 'monospace' }}>
                                {Math.ceil((plot.crop.duration - (Date.now() - plot.crop.plantedAt)) / 1000)}s
                            </div>
                        )}
                    </div>

                    {/* MUTATE ACTION */}
                    {canMutate && !isReady && !plot.crop.isMutated && (
                        <div
                            onClick={(e) => {
                                e.stopPropagation();
                                onMutate();
                            }}
                            style={{
                                marginTop: '4px',
                                background: 'rgba(239, 68, 68, 0.2)',
                                border: '1px solid rgba(239, 68, 68, 0.5)',
                                borderRadius: '4px',
                                padding: '2px 8px',
                                fontSize: '0.7rem',
                                color: '#fca5a5',
                                cursor: 'pointer',
                                transition: 'all 0.2s',
                                zIndex: 10
                            }}
                            className="hover:bg-red-900/40"
                        >
                            Mutate (100g)
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};


export const FarmingPanel = () => {
    const skills = useStore((state) => state.skills);
    const farming = useStore((state) => state.farming);
    const plantAction = useStore((state) => state.plantAction);
    const harvestAction = useStore((state) => state.harvestAction);

    // Use a time-based tick for smooth progress animation
    const [, setTick] = useState(0);
    const rafRef = useRef<number | null>(null);

    useEffect(() => {
        let lastTime = performance.now();
        const animate = (currentTime: number) => {
            // Update at ~30fps for smooth progress bars
            if (currentTime - lastTime >= 33) {
                lastTime = currentTime;
                setTick(t => t + 1);
            }
            rafRef.current = requestAnimationFrame(animate);
        };
        rafRef.current = requestAnimationFrame(animate);
        return () => {
            if (rafRef.current !== null) {
                cancelAnimationFrame(rafRef.current);
            }
        };
    }, []);

    const [selectedSeed, setSelectedSeed] = useState<CropType>('potato');

    const currentFarmingLevel = skills.farming?.level ?? 1;
    const currentFarmingXp = skills.farming?.xp ?? 0;
    const nextFarmingLevelXp = 50 * Math.pow(currentFarmingLevel, 2);
    const currentFarmingLevelStartXp = 50 * Math.pow(currentFarmingLevel - 1, 2);
    const farmingXpNeeded = nextFarmingLevelXp - currentFarmingLevelStartXp;
    const farmingXpProgress = currentFarmingXp - currentFarmingLevelStartXp;
    const farmingProgressPercent = Math.min(100, Math.max(0, (farmingXpProgress / farmingXpNeeded) * 100));

    const seeds = FARMING_DATA;

    const rainTotem = farming.rainTotem;
    const canCastRain = rainTotem?.unlocked && (Date.now() >= rainTotem.lastCast + rainTotem.cooldown);
    const rainCooldown = rainTotem ? Math.max(0, (rainTotem.lastCast + rainTotem.cooldown) - Date.now()) : 0;

    // Format timer
    const formatTime = (ms: number) => {
        const s = Math.ceil(ms / 1000);
        const m = Math.floor(s / 60);
        return `${m}:${(s % 60).toString().padStart(2, '0')}`;
    };

    const activeGnomes = farming.plots.filter(p => p.assignedGnome).length;
    const maxGnomes = farming.gnomeHuts?.unlocked ?? 0;

    const assignGnome = useStore((state) => state.assignGnome);
    const removeGnome = useStore((state) => state.removeGnome);
    const castRainTotem = useStore((state) => state.castRainTotem);
    const mutateCrop = useStore((state) => state.mutateCrop);

    // Helper to determine if a plot is unlocked based on level (UI purely, actual logic in store)
    // Actually store has `unlocked` boolean. The level check is just for the "Unlock at Lvl X" text.

    return (
        <Card
            title="Farming"
            headerAction={
                <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                    {rainTotem?.unlocked && (
                        <Button
                            variant="primary"
                            disabled={!canCastRain}
                            onClick={castRainTotem}
                            style={{ padding: '4px 8px', fontSize: '0.8rem', display: 'flex', gap: '6px', alignItems: 'center' }}
                            title="Advance all crops by 15 mins"
                        >
                            <CloudRain size={16} />
                            {canCastRain ? 'Summon Rain' : formatTime(rainCooldown)}
                        </Button>
                    )}
                    <div className="level-badge" style={{ background: 'var(--color-skill-farming)' }}>Lvl {currentFarmingLevel}</div>
                </div>
            }
        >
            <div className="xp-container" style={{ marginBottom: '24px' }}>
                <div className="xp-bar-bg">
                    <div
                        className="xp-bar-fill"
                        style={{
                            width: `${farmingProgressPercent}%`,
                            background: 'var(--color-skill-farming)'
                        }}
                    />
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '4px', fontSize: '0.8rem', color: 'var(--color-text-secondary)' }}>
                    <span>Level {currentFarmingLevel}</span>
                    <span>{currentFarmingXp.toLocaleString()} / {nextFarmingLevelXp.toLocaleString()} XP</span>
                    <span>Level {currentFarmingLevel + 1}</span>
                </div>
            </div>

            {/* GNOME STATUS */}
            {maxGnomes > 0 && (
                <div style={{
                    marginBottom: '16px',
                    fontSize: '0.9rem',
                    color: 'var(--color-text-secondary)',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    background: 'var(--color-bg-panel-hover)',
                    padding: '8px 12px',
                    borderRadius: '8px'
                }}>
                    <span>🧙‍♂️ Gnome Helpers:</span>
                    <span style={{ color: activeGnomes < maxGnomes ? 'var(--color-text-primary)' : 'var(--color-warning)', fontWeight: 700 }}>
                        {activeGnomes} / {maxGnomes}
                    </span>
                </div>
            )}

            <div className="farming-controls">
                {/* Seed Selector - Horizontal Scroll with clean look */}
                <div className="seed-selector" style={{
                    display: 'flex',
                    gap: '8px',
                    marginBottom: '24px',
                    overflowX: 'auto',
                    paddingBottom: '8px'
                }}>
                    {seeds.map(seed => (
                        <Button
                            key={seed.id}
                            variant={selectedSeed === seed.id ? 'primary' : 'outline'}
                            onClick={() => setSelectedSeed(seed.id as CropType)}
                            disabled={currentFarmingLevel < seed.level}
                            style={{
                                borderColor: selectedSeed === seed.id ? seed.color : 'var(--color-border)',
                                background: selectedSeed === seed.id ? seed.color : 'transparent',
                                color: selectedSeed === seed.id ? '#fff' : 'var(--color-text-secondary)',
                                flexShrink: 0,
                                minWidth: '100px',
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                gap: '4px',
                                padding: '8px',
                                opacity: currentFarmingLevel < seed.level ? 0.5 : 1
                            }}
                        >
                            <span style={{ fontWeight: 700 }}>{seed.name}</span>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '0.75rem', opacity: 0.9 }}>
                                <span>{seed.cost}</span>
                                <Coins size={10} color="currentColor" />
                            </div>
                            {currentFarmingLevel < seed.level && (
                                <div style={{ fontSize: '0.7rem', color: 'var(--color-danger)' }}>Lvl {seed.level}</div>
                            )}
                        </Button>
                    ))}
                </div>
            </div>

            <div className="farming-plot-grid">
                {farming.plots.map(plot => {
                    const isReady = plot.crop && Date.now() >= plot.crop.plantedAt + plot.crop.duration;

                    return (
                        <FarmingPlot
                            key={plot.id}
                            plot={plot}
                            selectedSeed={selectedSeed}
                            skills={skills}
                            maxGnomes={maxGnomes}
                            canMutate={farming.mutator?.unlocked ?? false}
                            onInteract={() => {
                                if (plot.crop && isReady) harvestAction(plot.id);
                                else if (!plot.crop) plantAction(plot.id, selectedSeed);
                            }}
                            onGnomeToggle={() => {
                                if (plot.assignedGnome) removeGnome(plot.id);
                                else assignGnome(plot.id);
                            }}
                            onMutate={() => mutateCrop(plot.id)}
                        />
                    );
                })}
            </div>

            {/* Legend / Tip */}
            {maxGnomes > 0 && (
                <div style={{ marginTop: '16px', fontSize: '0.8rem', color: 'var(--color-text-muted)', textAlign: 'center', fontStyle: 'italic' }}>
                    Tip: Assign Gnomes to automate planting and harvesting!
                </div>
            )}
        </Card>
    );
};
